import React, {useEffect, useState} from 'react';
import {
  Grid,
  Card,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Input,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from '@mui/material';

import {useForm} from 'react-hook-form';
// Removed the Link since we want everything in one file
import moment from 'moment';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import {Axios} from 'services/axios';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';
import {Loading, Notify} from 'notiflix';
import {ENDPOINT} from 'constants';
import {useNavigate} from 'react-router-dom';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Category Name', accessor: 'name', width: '45%', align: 'left'},
  {
    Header: 'Category Description',
    accessor: 'description',
    width: '45%',
    align: 'left',
  },
  {Header: 'Created at', accessor: 'createdOn', align: 'center'},
  {Header: 'Updated at', accessor: 'updatedOn', align: 'center'},
  {Header: 'status', accessor: 'status', align: 'center'},
  {Header: 'action', accessor: 'action', align: 'center'},
];

// We will create a handler to open the dialog with the selected ID
// and remove the Link to a separate page.
const getRow = (row, idx, handleEdit, handleNavigateToFeatures, canUpdate) => ({
  sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
  name: (
    <MDTypography display="block" variant="button" fontWeight="medium">
      {row.name}
    </MDTypography>
  ),
  description: <MDTypography variant="caption">{row.description}</MDTypography>,
  createdOn: (
    <MDTypography variant="caption">
      {moment(row.createdAt).calendar()}
    </MDTypography>
  ),
  updatedOn: (
    <MDTypography variant="caption">
      {moment(row.updatedAt).calendar()}
    </MDTypography>
  ),
  status: (
    <MDBox ml={-1}>
      <MDBadge
        badgeContent={row.active ? 'active' : 'inactive'}
        color={row.active ? 'success' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    </MDBox>
  ),
  action: (
    <MDBox ml={-1}>
      {canUpdate && (
        <MDBadge
          badgeContent="edit"
          color="info"
          variant="gradient"
          size="sm"
          // onClick triggers the same dialog for editing
          onClick={() => handleEdit(row.id)}
          style={{cursor: 'pointer'}}
        />
      )}
      {canUpdate && (
        <MDBadge
          badgeContent="Features"
          color="warning"
          variant="gradient"
          size="sm"
          // onClick triggers the same dialog for editing
          onClick={() => handleNavigateToFeatures(row.id)}
          style={{cursor: 'pointer'}}
        />
      )}
    </MDBox>
  ),
});

const Categories = () => {
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const [categories, setCategories] = useState([]);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // State for the "active" switch
  const [active, setActive] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: selectedCategory ? selectedCategory.name : '',
      slug: selectedCategory ? selectedCategory.slug : '',
      description: selectedCategory ? selectedCategory.description : '',
      image_url: selectedCategory ? selectedCategory.image_url : '',
      image: null,
      category_type_id: selectedCategory ? selectedCategory.category_type_id : '',
      active: selectedCategory ? selectedCategory.active : true,
    },
  });

  const navigate = useNavigate();

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      const response = await Axios.fetch('/shop/getAllCategories');
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch all category types (Start/Sell/Market) for the dropdown
  const fetchCategoryTypes = async () => {
    try {
      const response = await Axios.fetch('/category-types/all');
      setCategoryTypes(response.data.data);
    } catch (error) {
      console.error('Error fetching category types:', error);
    }
  };

  // Call fetch on component mount
  useEffect(() => {
    if (!canRead) return;
    fetchCategories();
    fetchCategoryTypes();
  }, [canRead]);

  // This function is called when user submits (Create or Update)
  const onSubmit = async data => {
    // Show loading indicator
    Loading.circle('Processing...');

    const formData = new FormData();

    formData.append('categoryId', selectedCategory?.id || ''); // If ID is present, it's an update

    formData.append('name', data.name);
    formData.append('slug', data.slug || '');
    formData.append('description', data.description);
    formData.append('active', active);
    // Home page services card: tag ("01 Build") and button label
    formData.append('card_tag', data.card_tag || '');
    formData.append('card_button_text', data.card_button_text || '');
    formData.append('card_link', data.card_link || '');
    if (data.category_type_id) {
      formData.append('category_type_id', data.category_type_id);
    }

    // formData.append('image_url', data.image_url || '');
    // formData.append('image', data.image[0] || '');

    if (data.image_url) {
      formData.append('image_url', data.image_url);
    } else if (data.image) {
      formData.append('image', data?.image[0] || '');
    }

    Axios.post(
      `/shop/addUpdateCategory${
        selectedCategory ? '?categoryId=' + selectedCategory?.id : ''
      }`,
      formData,
      {
        'Content-type': 'multipart/form-data',
      },
    )
      .then(res => {
        console.log(res);
        Notify.success(res.data?.message);
      })
      .catch(err => {
        console.error(err);
        Notify.failure(err.response?.data?.message);
      })
      .finally(() => {
        setOpenDialog(false);
        fetchCategories();

        Loading.remove('Processing...');
      });
  };

  // Fetch details of a selected category by ID and fill the dialog
  const fetchCategoryDetails = async categoryId => {
    try {
      const response = await fetch(
        `${ENDPOINT}api/v1/admin/shop/getCategoryById/${categoryId}`,
      );
      const data = await response.json();
      if (data.success) {
        setSelectedCategory(data.data);

        // Populate form fields for editing
        setValue('name', data.data.name);
        setValue('slug', data.data.slug || '');
        setValue('description', data.data.description);
        setValue('image_url', data.data.image_url || '');
        setValue('category_type_id', data.data.category_type_id || data.data.categoryType?.id || '');
        setValue('card_tag', data.data.card_tag || '');
        setValue('card_button_text', data.data.card_button_text || '');
        setValue('card_link', data.data.card_link || '');

        // We set "image" to null, so user can choose a new file if they want
        setValue('image', null);

        // Open dialog for editing
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('Error fetching category details:', error);
    }
  };

  // Handler for the "Edit" button in the table
  const handleEdit = categoryId => {
    fetchCategoryDetails(categoryId);
  };

  // Handler for the "Features" button in the table
  const handleNavigateToFeatures = categoryId => {
    navigate(`features/${categoryId}`);
    console.log('Navigate to features for category:', categoryId);
  };

  // Handler for opening a blank dialog (create mode)
  const handleAddNew = () => {
    // Clear previously selected category and form fields
    setSelectedCategory(null);
    reset();
    setOpenDialog(true);
  };

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to shop management. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                <MDTypography variant="h6" color="white">
                  Category List
                </MDTypography>
                {canCreate && (
                  <MDButton
                    variant="contained"
                    onClick={handleAddNew}
                    //style={{marginBottom: '20px'}}
                  >
                    Add New Category
                  </MDButton>
                )}
              </MDBox>
              <MDBox px={3} pt={3} pb={1} display="flex" alignItems="center" justifyContent="space-between">
                <TextField
                  placeholder="Search categories…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  size="small"
                  sx={{
                    width: 300,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      background: '#f8fafc',
                      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                    },
                  }}
                  InputProps={{ startAdornment: <span style={{ marginRight: 6, color: '#94a3b8' }}>🔍</span> }}
                />
              </MDBox>
              <MDBox pt={1}>
                <DataTable
                  table={{
                    columns,
                    rows: categories
                      .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase()))
                      .map((row, idx) => getRow(row, idx, handleEdit, handleNavigateToFeatures, canUpdate)),
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  hoverEffect
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Form Dialog (Create & Update) */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {selectedCategory
            ? `Edit Category- ${selectedCategory?.name}`
            : 'Create Category'}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* NAME */}
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              {...register('name', {required: 'Name is required'})}
              error={!!errors.name}
              helperText={errors.name?.message}
            />

            {/* SLUG (optional — auto-generated from name if left blank) */}
            <TextField
              label="Slug"
              fullWidth
              margin="normal"
              helperText="Optional — leave blank to auto-generate from Name"
              {...register('slug')}
            />

            {/* CATEGORY TYPE (Start / Sell / Market) */}
            <FormControl fullWidth margin="normal">
              <InputLabel id="category-type-label">Category Type</InputLabel>
              <Select
                labelId="category-type-label"
                label="Category Type"
                value={watch('category_type_id') || ''}
                onChange={e => setValue('category_type_id', e.target.value)}
              >
                {categoryTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* DESCRIPTION */}
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              {...register('description', {
                required: 'Description is required',
              })}
              error={!!errors.description}
              helperText={errors.description?.message}
            />

            {/* HOME SERVICES CARD (optional) */}
            <TextField
              label="Card Tag (Home page)"
              fullWidth
              margin="normal"
              placeholder="e.g. Build"
              inputProps={{maxLength: 40}}
              helperText='Shown as "01 Build" on the home page services card. The number is added automatically.'
              {...register('card_tag')}
            />

            <TextField
              label="Card Button Text (Home page)"
              fullWidth
              margin="normal"
              placeholder="e.g. Build My Website"
              inputProps={{maxLength: 60}}
              helperText="Button label on the home page services card. Leave blank for the default."
              {...register('card_button_text')}
            />

            <TextField
              label="Card Button Link (Home page)"
              fullWidth
              margin="normal"
              placeholder="e.g. /services/website-design"
              inputProps={{maxLength: 500}}
              helperText="Where the card button goes. A page path (/services/seo) or a full URL. Leave blank for the default."
              {...register('card_link')}
            />

            {/* IMAGE URL (optional) */}
            <TextField
              label="Image URL"
              fullWidth
              margin="normal"
              {...register('image_url')}
            />

            {/* IMAGE FILE (optional) */}
            <Input
              type="file"
              fullWidth
              margin="normal"
              // For MUI <Input>, "margin" won't affect as it does for TextField
              {...register('image')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={e => setActive(e.target.checked)}
                  name="active"
                  color="primary"
                />
              }
              label="Active"
              sx={{mt: 2}}
            />

            <DialogActions>
              <MDButton
                onClick={() => setOpenDialog(false)}
                color="secondary"
                variant="outlined">
                Cancel
              </MDButton>
              <MDButton type="submit" color="info" variant="contained">
                {selectedCategory ? 'Update Category' : 'Add Category'}
              </MDButton>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Categories;
